import html from "./dataset-chooser.html?raw"
import styles from "./dataset-chooser.css?inline"
import { sharedStyles } from "../../shared-styles"
import "../file-chooser"
import "../url-chooser"
import type { FileChangeEvent } from "../file-chooser"
import type { URLChangeEvent, URLChooser } from "../url-chooser"
import { showFileSize } from "../file-chooser"
import { deriveFilename } from "./filename"
import { fetchRdf, ACCEPT_HEADER, type RdfFormat } from "graph-store"

// Media types url-chooser will accept during interactive validation: every
// RDF serialization graph-store can ingest (single source of truth — see
// ACCEPT_HEADER), plus the generic carriers servers commonly serve `.ttl`
// under. fetchRdf defaults unrecognized content types to Turtle, so these
// generic types still load.
const URL_ACCEPT = [
  ...ACCEPT_HEADER.split(",").map((t) => t.trim()),
  "application/x-turtle",
  "text/plain",
  "application/octet-stream",
]

// Largest dataset we'll attempt to load. ~3-5x indexing overhead in Oxigraph
// (in-memory WASM build) puts the in-memory footprint near iOS Safari's
// typical per-tab ceiling (~1-1.5 GB). Above this we just refuse.
export const MAX_DATASET_BYTES = 250 * 1024 * 1024

// Minimum interval between loading byte-readout updates. The download reports
// progress per chunk (many times a second); throttling to a steady cadence
// (paired with tabular-nums digits in CSS) keeps the counter smooth rather than
// jittery.
const LOADING_UPDATE_MS = 125

interface DatasetChangeEventDetail {
  file: File
  // RDF serialization detected by fetchRdf (URL loads). Omitted for local file
  // loads, where the format is detected downstream from the file name/type.
  format?: RdfFormat
  sourceUrl?: string
}

export interface DatasetChangeEvent extends CustomEvent {
  detail: DatasetChangeEventDetail
}

export interface LoadedMetadata {
  name: string
  size: number
  sourceUrl?: string
}

class DatasetChooser extends HTMLElement {
  private shadow: ShadowRoot
  // Timestamp of the last loading byte-readout render, for throttling (see
  // LOADING_UPDATE_MS).
  private lastLoadingUpdate = 0

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: "open" })
    this.shadow.adoptedStyleSheets = [sharedStyles]
    const template = document.createElement("template")
    template.innerHTML = `<style>${styles}</style>${html}`
    this.shadow.appendChild(template.content.cloneNode(true))
  }

  connectedCallback() {
    const fileChooser = this.shadow.querySelector("file-chooser")!
    const urlChooser = this.shadow.querySelector<URLChooser>("url-chooser")!
    const clear = this.shadow.querySelector<HTMLButtonElement>("#clear")!

    urlChooser.maxBytes = MAX_DATASET_BYTES
    urlChooser.accept = URL_ACCEPT

    // Relocate any slotted url-list datalist into the inner url-chooser's
    // light DOM. Slot forwarding through the shadow boundary doesn't propagate
    // slotchange events into the inner component, so url-chooser would never
    // see the datalist. Moving the node sidesteps that.
    for (const child of Array.from(this.children)) {
      if (child.getAttribute("slot") === "url-list") {
        urlChooser.appendChild(child)
      }
    }

    fileChooser.addEventListener("change", (event) => {
      const file = (event as FileChangeEvent).detail.file
      // Emit only — the consumer drives the preview through the parse lifecycle
      // (setParsing → setLoaded). Rendering the loaded preview here would flash a
      // premature "loaded" state before parsing has even started.
      this.emitChange(file)
    })

    urlChooser.addEventListener("change", async (event) => {
      const { url, credentials } = (event as URLChangeEvent).detail
      try {
        // graph-store's fetchRdf owns the download mechanics: Accept
        // negotiation (ACCEPT_HEADER), the mid-stream byte cap, progress, and
        // Content-Type → format detection. We keep policy/UI here: the
        // MAX_DATASET_BYTES ceiling, Basic auth, progress display, the derived
        // filename, and friendly error surfacing.
        const headers: Record<string, string> = {}
        if (credentials !== null) {
          headers.Authorization =
            "Basic " + btoa(`${credentials.user}:${credentials.pass}`)
        }
        this.setLoading()
        const { bytes, format } = await fetchRdf(url.toString(), {
          headers,
          maxBytes: MAX_DATASET_BYTES,
          onProgress: (n, total) => this.setLoading({ bytes: n, total }),
        })
        const file = new File([bytes], deriveFilename(url), {
          type: "text/turtle",
        })
        const sourceUrl = url.toString()
        // Emit only — the consumer drives setParsing → setLoaded. (We still own
        // the "Loading …" UI above, since the download is ours.) Rendering the
        // loaded preview here would flash a premature "loaded" state between
        // loading and parsing.
        this.emitChange(file, format, sourceUrl)
      } catch (error) {
        console.error(error)
        this.setError(error instanceof Error ? error.message : String(error))
      }
    })

    clear.addEventListener("click", () => {
      this.dispatchEvent(new CustomEvent("clear"))
    })
  }

  setLoaded(meta: LoadedMetadata) {
    this.renderPreview(meta)
  }

  setLoading(progress: { bytes?: number; total?: number } = {}) {
    if (progress.bytes !== undefined) {
      // Throttle byte updates so the counter ticks at a steady, readable rate
      // instead of churning on every chunk. (tabular-nums in CSS keeps the
      // digits from shifting width as they change.)
      const now = performance.now()
      if (now - this.lastLoadingUpdate < LOADING_UPDATE_MS) return
      this.lastLoadingUpdate = now
    } else {
      // Fresh indeterminate render — let the first byte update show at once.
      this.lastLoadingUpdate = 0
    }
    const preview = this.shadow.querySelector<HTMLElement>("#preview")!
    preview.classList.remove("error")
    preview.replaceChildren(this.loadingParagraph(progress))
    this.shadow.querySelector<HTMLButtonElement>("#clear")!.hidden = true
  }

  // A plain static "Loading …" (the byte readout is the progress indicator, so
  // animated dots alongside the changing numbers read as too busy). When a total
  // is known, the running value sits right-aligned in a slot whose width is
  // pinned to the total, so the trailing " of TOTAL)" never shifts as the value
  // grows in width.
  private loadingParagraph(progress: { bytes?: number; total?: number }) {
    const p = document.createElement("p")
    if (progress.bytes !== undefined && progress.total !== undefined) {
      const total = showFileSize(progress.total)
      const slot = document.createElement("span")
      slot.className = "done-slot"
      // The sizer reserves the total's width (it stays in flow but hidden); the
      // value overlays it, right-aligned. aria-hidden keeps the duplicated text
      // out of the accessibility tree.
      const sizer = document.createElement("span")
      sizer.className = "sizer"
      sizer.setAttribute("aria-hidden", "true")
      sizer.textContent = total
      const value = document.createElement("span")
      value.className = "value"
      value.textContent = showFileSize(progress.bytes)
      slot.append(sizer, value)
      p.append("Loading … (", slot, ` of ${total})`)
    } else if (progress.bytes !== undefined) {
      p.append(`Loading … (${showFileSize(progress.bytes)})`)
    } else {
      p.append("Loading …")
    }
    return p
  }

  setParsing() {
    // Oxigraph parses the whole document in one WASM call, so there is no
    // byte-by-byte progress to report (unlike the download). The animated dots
    // (CSS .dots::after — a static "…" under reduced motion) stand in for the
    // missing progress indicator, signalling the parse is still running.
    const preview = this.shadow.querySelector<HTMLElement>("#preview")!
    preview.classList.remove("error")
    const p = document.createElement("p")
    p.className = "progress"
    const label = document.createElement("span")
    label.className = "label"
    label.textContent = "Parsing"
    const dots = document.createElement("span")
    dots.className = "dots"
    p.append(label, " ", dots)
    preview.replaceChildren(p)
    this.shadow.querySelector<HTMLButtonElement>("#clear")!.hidden = true
  }

  openUrlChooser(url: string) {
    this.shadow.querySelector<URLChooser>("url-chooser")!.openWithUrl(url)
  }

  setError(message: string) {
    const preview = this.shadow.querySelector<HTMLElement>("#preview")!
    preview.classList.add("error")
    preview.replaceChildren(
      this.previewParagraph(`Load failed: ${message}`, message),
    )
    this.shadow.querySelector<HTMLButtonElement>("#clear")!.hidden = true
  }

  reset() {
    const preview = this.shadow.querySelector<HTMLElement>("#preview")!
    preview.classList.remove("error")
    preview.replaceChildren(this.previewParagraph("No data loaded"))
    this.shadow.querySelector<HTMLButtonElement>("#clear")!.hidden = true
  }

  private emitChange(file: File, format?: RdfFormat, sourceUrl?: string) {
    this.dispatchEvent(
      new CustomEvent<DatasetChangeEventDetail>("change", {
        detail: {
          file,
          ...(format !== undefined ? { format } : {}),
          ...(sourceUrl !== undefined ? { sourceUrl } : {}),
        },
      }),
    )
  }

  private renderPreview(meta: LoadedMetadata) {
    const preview = this.shadow.querySelector<HTMLElement>("#preview")!
    preview.classList.remove("error")
    const base = `${meta.name} (${showFileSize(meta.size)})`
    let text = base
    let title: string | undefined
    if (meta.sourceUrl) {
      title = meta.sourceUrl
      let hostname = meta.sourceUrl
      try {
        hostname = new URL(meta.sourceUrl).hostname
      } catch {
        // unparseable — keep the raw string
      }
      text = `${base} — ${hostname}`
    }
    preview.replaceChildren(this.previewParagraph(text, title))
    this.shadow.querySelector<HTMLButtonElement>("#clear")!.hidden = false
  }

  private previewParagraph(text: string, title?: string) {
    const p = document.createElement("p")
    p.textContent = text
    if (title) p.title = title
    return p
  }
}

customElements.define("dataset-chooser", DatasetChooser)
export { DatasetChooser }
